import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  ArrowLeft, Calendar, User, Heart, MessageCircle, Share2, 
  BookOpen, Leaf, Globe, Users, ChevronRight, Clock, Search, X
} from "lucide-react";
import { Input } from "../components/ui/input";

const ProBlogPage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Blog posts with diverse urban gardening content
  const blogPosts = [
    {
      id: 1,
      title: "Growing Together: Community Gardens Transforming Urban Spaces",
      excerpt: "Discover how neighborhoods are coming together to create thriving green spaces that provide fresh produce, build connections, and beautify city blocks.",
      image: "https://images.pexels.com/photos/5029775/pexels-photo-5029775.jpeg?auto=compress&cs=tinysrgb&w=800",
      author: "Maria Santos",
      date: "August 2026",
      category: "Community",
      readTime: "5 min read",
      likes: 234,
      comments: 45
    },
    {
      id: 2,
      title: "Balcony Farming: Maximum Harvest in Minimum Space",
      excerpt: "Learn vertical gardening techniques that turn small apartment balconies into productive food sources. Tips from urban growers around the world.",
      image: "https://images.unsplash.com/photo-1778569950085-84e3fd790663?auto=compress&w=800",
      author: "Keisha Williams",
      date: "July 2026",
      category: "Small Space",
      readTime: "7 min read",
      likes: 189,
      comments: 32
    },
    {
      id: 3,
      title: "Sustainable Practices: Composting in the Caribbean",
      excerpt: "How island communities are turning kitchen waste into garden gold while reducing landfill impact and growing healthier crops.",
      image: "https://images.pexels.com/photos/8084701/pexels-photo-8084701.jpeg?auto=compress&cs=tinysrgb&w=800",
      author: "Andre Jackson",
      date: "June 2026",
      category: "Sustainability",
      readTime: "6 min read",
      likes: 312,
      comments: 67
    },
    {
      id: 4,
      title: "From Seed to Table: A Family Garden Journey",
      excerpt: "Follow one family as they transform their backyard into a thriving food garden, teaching their children where food really comes from.",
      image: "https://images.unsplash.com/photo-1509099381441-ea3c0cf98b94?auto=compress&w=800",
      author: "Fatima Okonkwo",
      date: "May 2026",
      category: "Family",
      readTime: "8 min read",
      likes: 278,
      comments: 54
    },
    {
      id: 5,
      title: "Rooftop Revolution: Urban Farms Above the City",
      excerpt: "Meet the pioneers turning unused rooftops into productive farms that supply restaurants, markets, and communities with fresh local produce.",
      image: "https://images.unsplash.com/photo-1744726010540-bf318d4a691f?auto=compress&w=800",
      author: "Chen Wei",
      date: "April 2026",
      category: "Urban Farming",
      readTime: "6 min read",
      likes: 421,
      comments: 89
    },
    {
      id: 6,
      title: "Healing Gardens: Growing for Mind and Body",
      excerpt: "Explore the therapeutic benefits of gardening and how communities are creating spaces that nurture both plants and people.",
      image: "https://images.pexels.com/photos/27176779/pexels-photo-27176779.jpeg?auto=compress&cs=tinysrgb&w=800",
      author: "Priya Sharma",
      date: "March 2026",
      category: "Wellness",
      readTime: "5 min read",
      likes: 356,
      comments: 71
    },
    {
      id: 7,
      title: "Youth Growing Leaders: School Garden Programs",
      excerpt: "How school gardens are teaching the next generation about nutrition, science, and environmental stewardship while building community pride.",
      image: "https://images.pexels.com/photos/9721352/pexels-photo-9721352.jpeg?auto=compress&cs=tinysrgb&w=800",
      author: "Jamal Thompson",
      date: "February 2026",
      category: "Education",
      readTime: "7 min read",
      likes: 445,
      comments: 93
    },
    {
      id: 8,
      title: "Water-Wise Gardening: Techniques for Dry Climates",
      excerpt: "Innovative irrigation methods and drought-resistant plants that help gardeners thrive even in water-scarce environments.",
      image: "https://images.unsplash.com/photo-1786206771315-c97fe1aebe69?auto=compress&w=800",
      author: "Rosa Martinez",
      date: "January 2026",
      category: "Sustainability",
      readTime: "6 min read",
      likes: 287,
      comments: 48
    }
  ];

  const categories = [
    { id: "all", label: "All Posts", icon: BookOpen },
    { id: "Community", label: "Community", icon: Users },
    { id: "Sustainability", label: "Sustainability", icon: Leaf },
    { id: "Urban Farming", label: "Urban Farming", icon: Globe },
    { id: "Education", label: "Education", icon: BookOpen },
  ];

  // Filter posts by category AND search query
  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === "all" || post.category === selectedCategory;
    const matchesSearch = searchQuery === "" || 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/")} data-testid="blog-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
                <Leaf className="w-6 h-6 text-primary" />
                Pro Blog
              </h1>
              <p className="text-sm text-muted-foreground">
                Stories from the garden
              </p>
            </div>
          </div>
          <Link to="/">
            <img src="/logo.png" alt="RootSpace" className="h-8 w-auto" />
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 px-4">
        <div className="absolute inset-0 bg-gradient-to-r from-green-600/90 to-green-700/90" />
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${blogPosts[0].image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative max-w-4xl mx-auto text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Badge className="bg-white/20 text-white mb-4">Monthly Updates</Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Growing Stories, Growing Community
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              Real stories from gardeners around the world. Discover tips, inspiration, 
              and the joy of growing your own food in any space.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search and Category Filter */}
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* Search Bar */}
        <div className="max-w-md mx-auto relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search topics, authors, or keywords..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10"
            data-testid="blog-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid="blog-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 justify-center">
          {categories.map(cat => {
            const Icon = cat.icon;
            return (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? "default" : "outline"}
                onClick={() => setSelectedCategory(cat.id)}
                className="flex items-center gap-2"
                data-testid={`blog-category-${cat.id}`}
              >
                <Icon className="w-4 h-4" />
                {cat.label}
              </Button>
            );
          })}
        </div>

        {/* Search Results Count */}
        {(searchQuery || selectedCategory !== "all") && (
          <p className="text-center text-sm text-muted-foreground" data-testid="blog-results-count">
            {filteredPosts.length} {filteredPosts.length === 1 ? 'article' : 'articles'} found
            {searchQuery && ` for "${searchQuery}"`}
            {selectedCategory !== "all" && ` in ${selectedCategory}`}
          </p>
        )}
      </div>

      {/* Blog Grid */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* No Results State */}
        {filteredPosts.length === 0 && (
          <div className="text-center py-16" data-testid="blog-no-results">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No articles found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search or filter to find what you&apos;re looking for.
            </p>
            <Button 
              variant="outline" 
              onClick={() => { setSearchQuery(""); setSelectedCategory("all"); }}
              data-testid="blog-clear-filters"
            >
              Clear all filters
            </Button>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPosts.map((post, index) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 group h-full">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <Badge className="absolute top-3 left-3 bg-primary/90">
                    {post.category}
                  </Badge>
                </div>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {post.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </span>
                  </div>
                  <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between pt-3 border-t">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-sm font-medium">{post.author}</span>
                    </div>
                    <div className="flex items-center gap-3 text-muted-foreground">
                      <span className="flex items-center gap-1 text-sm">
                        <Heart className="w-4 h-4" />
                        {post.likes}
                      </span>
                      <span className="flex items-center gap-1 text-sm">
                        <MessageCircle className="w-4 h-4" />
                        {post.comments}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Load More */}
        <div className="text-center mt-12">
          <Button variant="outline" size="lg" className="gap-2">
            Load More Stories
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </main>

      {/* Newsletter CTA */}
      <section className="bg-primary/5 py-16 px-4 mt-12">
        <div className="max-w-2xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">Stay Connected</h3>
          <p className="text-muted-foreground mb-6">
            Get monthly gardening tips, community stories, and seasonal planting guides 
            delivered to your inbox.
          </p>
          <div className="flex gap-3 max-w-md mx-auto">
            <input 
              type="email"
              placeholder="your@email.com"
              className="flex-1 px-4 py-2 rounded-lg border border-input bg-background"
            />
            <Button>Subscribe</Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-white/60 text-sm">
            &copy; 2026 RootSpace.Pro - Sustainable gardens for a better world.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ProBlogPage;
